import { useState, useEffect, useRef } from 'react';
import HellometerLogoBoxy from '../../assets/images/hellometer/logo-boxy.png';
import { cn } from '@/lib/utils';

export default function HellometerLoading(): JSX.Element {
  const [gameActive, setGameActive] = useState(false);
  const [counter, setCounter] = useState(0);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pacmanRef = useRef({
    x: 0,
    y: 0,
    dir: 'left' as 'left' | 'right' | 'up' | 'down',
    speed: 1,
    size: 10,
    mouthAngle: 0,
  });
  const ghostRef = useRef({
    x: 20,
    y: 80,
    dir: 'right' as 'left' | 'right' | 'up' | 'down',
    speed: 0.75,
    size: 10,
    color: 'red',
  });
  const animationRef = useRef<number | null>(null);
  const widthRef = useRef(0);
  const heightRef = useRef(0);
  const pathPositionsRef = useRef<{ x: number; y: number }[]>([]);
  const dotPositionRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!gameActive && e.key === 'ArrowLeft') {
        setGameActive(true);
      } else if (gameActive) {
        switch (e.key) {
          case 'ArrowLeft':
            pacmanRef.current.dir = 'left';
            break;
          case 'ArrowRight':
            pacmanRef.current.dir = 'right';
            break;
          case 'ArrowUp':
            pacmanRef.current.dir = 'up';
            break;
          case 'ArrowDown':
            pacmanRef.current.dir = 'down';
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameActive]);

  useEffect(() => {
    if (!gameActive) return;

    const image = new Image();
    image.src = HellometerLogoBoxy;
    image.onload = () => {
      widthRef.current = image.width;
      heightRef.current = image.height;

      // Set pacman starting position in the dot area (top right), moving left into the track
      pacmanRef.current.x = image.width * 0.95;
      pacmanRef.current.y = image.height * 0.1;
      pacmanRef.current.dir = 'left';

      dotPositionRef.current.x = image.width * 0.95;
      dotPositionRef.current.y = image.height * 0.1;

      // Precompute path positions in the H shape
      const pathPositions = [];
      for (let y = 0; y < image.height; y++) {
        for (let x = 0; x < image.width; x++) {
          if (isPath(x, y, image.width, image.height)) {
            pathPositions.push({ x, y });
          }
        }
      }
      pathPositionsRef.current = pathPositions;

      // Set initial ghost position to a random path position
      if (pathPositions.length > 0) {
        let pos;
        do {
          pos = pathPositions[Math.floor(Math.random() * pathPositions.length)];
        } while (
          Math.abs(pos.x - pacmanRef.current.x) < 20 &&
          Math.abs(pos.y - pacmanRef.current.y) < 20
        );
        ghostRef.current.x = pos.x;
        ghostRef.current.y = pos.y;
        ghostRef.current.dir = getRandomAllowedDir(ghostRef.current.x, ghostRef.current.y, image.width, image.height);
        ghostRef.current.color = ['red', 'lightgreen', 'navy'][Math.floor(Math.random() * 3)];
      }

      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = image.width;
      canvas.height = image.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const animate = () => {
        if (!ctx) return;

        // Move Pacman
        moveEntity(pacmanRef.current, image.width, image.height, pathPositionsRef.current);

        // Move Ghost
        moveEntity(ghostRef.current, image.width, image.height, pathPositionsRef.current, true);

        // Check collision
        const p = pacmanRef.current;
        const g = ghostRef.current;
        const dist = Math.sqrt((p.x - g.x) ** 2 + (p.y - g.y) ** 2);
        if (dist < p.size + g.size) {
          setCounter((prev) => prev + 1);
          respawnGhost();
        }

        // Draw
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(image, 0, 0);

        // Draw counter in the dot of the i
        ctx.font = 'bold 12px Arial';
        ctx.fillStyle = 'navy';
        ctx.textAlign = 'center';
        ctx.fillText(`${counter}`, dotPositionRef.current.x - (0.04 * widthRef.current), dotPositionRef.current.y + (0.02 * heightRef.current) + 4);

        // Draw Pacman
        const pacmanColor = '#1E90FF';
        const mouthOpen = 0.2 * Math.PI * Math.sin(p.mouthAngle);
        let offsetAngle = 0;
        if (p.dir === 'left') offsetAngle = Math.PI;
        else if (p.dir === 'up') offsetAngle = 1.5 * Math.PI;
        else if (p.dir === 'down') offsetAngle = 0.5 * Math.PI;

        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.arc(p.x, p.y, p.size, mouthOpen + offsetAngle, 2 * Math.PI - mouthOpen + offsetAngle);
        ctx.lineTo(p.x, p.y);
        ctx.fillStyle = pacmanColor;
        ctx.fill();

        // Pacman eye
        ctx.beginPath();
        const eyeOffset = p.size / 3;
        const eyeX = p.x + eyeOffset * Math.cos(offsetAngle);
        const eyeY = p.y + eyeOffset * Math.sin(offsetAngle) - eyeOffset;
        ctx.arc(eyeX, eyeY, p.size / 5, 0, 2 * Math.PI);
        ctx.fillStyle = 'black';
        ctx.fill();

        // Draw Ghost
        ctx.fillStyle = g.color;
        ctx.beginPath();
        ctx.arc(g.x, g.y, g.size, Math.PI, 0, false);
        ctx.lineTo(g.x + g.size, g.y);
        ctx.lineTo(g.x + g.size, g.y + g.size);
        ctx.lineTo(g.x + (g.size / 3) * 2, g.y + g.size * 0.75);
        ctx.lineTo(g.x + g.size / 3, g.y + g.size);
        ctx.lineTo(g.x, g.y + g.size * 0.75);
        ctx.lineTo(g.x - g.size / 3, g.y + g.size);
        ctx.lineTo(g.x - (g.size / 3) * 2, g.y + g.size * 0.75);
        ctx.lineTo(g.x - g.size, g.y + g.size);
        ctx.lineTo(g.x - g.size, g.y);
        ctx.closePath();
        ctx.fill();

        // Ghost eyes
        const eyeSize = g.size / 3;
        ctx.fillStyle = 'white';
        ctx.beginPath();
        ctx.arc(g.x - eyeSize, g.y, eyeSize, 0, 2 * Math.PI);
        ctx.arc(g.x + eyeSize, g.y, eyeSize, 0, 2 * Math.PI);
        ctx.fill();
        ctx.fillStyle = 'blue';
        ctx.beginPath();
        ctx.arc(g.x - eyeSize, g.y, eyeSize / 2, 0, 2 * Math.PI);
        ctx.arc(g.x + eyeSize, g.y, eyeSize / 2, 0, 2 * Math.PI);
        ctx.fill();

        animationRef.current = requestAnimationFrame(animate);
      };

      animate();
    };

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [gameActive, counter]);

  const respawnGhost = () => {
    const pathPositions = pathPositionsRef.current;
    if (pathPositions.length > 0) {
      let pos;
      do {
        pos = pathPositions[Math.floor(Math.random() * pathPositions.length)];
      } while (
        Math.abs(pos.x - pacmanRef.current.x) < 20 &&
        Math.abs(pos.y - pacmanRef.current.y) < 20
      );
      ghostRef.current.x = pos.x;
      ghostRef.current.y = pos.y;
      ghostRef.current.dir = getRandomAllowedDir(ghostRef.current.x, ghostRef.current.y, widthRef.current, heightRef.current);
      ghostRef.current.color = ['red', 'lightgreen', 'navy'][Math.floor(Math.random() * 3)];
    }
  };

  return (
    <div className="flex items-center justify-center w-full h-full p-4">
      {!gameActive ? (
        <img
          src={HellometerLogoBoxy}
          className={cn('animate-pulse w-32 md:w-40')}
          alt="Loading..."
        />
      ) : (
        <canvas ref={canvasRef} className={cn('w-32 md:w-40')} />
      )}
    </div>
  );
}

function isPath(x: number, y: number, width: number, height: number): boolean {
  const left_start = 0.2 * width;
  const right_end = 0.8 * width;
  const bar_width = 0.05 * width;
  const cross_start = 0.45 * height;
  const cross_end = 0.55 * height;

  const in_left_vertical = x >= left_start && x <= left_start + bar_width;
  const in_right_vertical = x >= right_end - bar_width && x <= right_end;
  const in_horizontal_cross = y >= cross_start && y <= cross_end && x >= left_start && x <= right_end;
  const in_top_right_connect = y <= 0.15 * height && x >= right_end - bar_width && x <= width;

  return in_left_vertical || in_right_vertical || in_horizontal_cross || in_top_right_connect;
}

function getAllowedDirs(x: number, y: number, width: number, height: number): string[] {
  const left_start = 0.2 * width;
  const bar_width = 0.05 * width;
  const right_end = 0.8 * width;
  const cross_start = 0.45 * height;
  const cross_end = 0.55 * height;

  const allowed: string[] = [];

  const is_vertical = (x >= left_start && x <= left_start + bar_width) || (x >= right_end - bar_width && x <= right_end);
  const is_horizontal_cross = y >= cross_start && y <= cross_end;
  const is_top_right_connect = y <= 0.15 * height && x >= right_end - bar_width && x <= width;

  if (is_vertical) {
    allowed.push('up', 'down');
  }
  if (is_horizontal_cross || is_top_right_connect) {
    allowed.push('left', 'right');
  }

  return allowed;
}

function getRandomAllowedDir(x: number, y: number, width: number, height: number): 'left' | 'right' | 'up' | 'down' {
  const allowed = getAllowedDirs(x, y, width, height);
  if (allowed.length === 0) return 'right'; // Fallback
  return allowed[Math.floor(Math.random() * allowed.length)] as 'left' | 'right' | 'up' | 'down';
}

function moveEntity(
  entity: any,
  width: number,
  height: number,
  pathPositions: { x: number; y: number }[],
  isGhost = false
) {
  const allowedDirs = getAllowedDirs(entity.x, entity.y, width, height);
  if (!allowedDirs.includes(entity.dir)) {
    return; // Direction not allowed in current section, don't move
  }

  let dx = 0,
    dy = 0;
  if (entity.dir === 'left') dx = -entity.speed;
  else if (entity.dir === 'right') dx = entity.speed;
  else if (entity.dir === 'up') dy = -entity.speed;
  else if (entity.dir === 'down') dy = entity.speed;

  let newX = entity.x + dx;
  let newY = entity.y + dy;

  // Handle vertical teleportation for left and right parts of H
  const left_start = 0.2 * width;
  const bar_width = 0.05 * width;
  const right_end = 0.8 * width;
  const is_left_vertical = (posX: number) => posX >= left_start && posX <= left_start + bar_width;
  const is_right_vertical = (posX: number) => posX >= right_end - bar_width && posX <= right_end;

  if (newY < 0 && (is_left_vertical(newX) || is_right_vertical(newX))) {
    newY += height;
  } else if (newY >= height && (is_left_vertical(newX) || is_right_vertical(newX))) {
    newY -= height;
  } else if (newY < 0 || newY >= height) {
    return; // Block if not in vertical
  }

  if (newX < 0 || newX >= width) {
    return; // Block horizontal edges
  }

  if (isPath(newX, newY, width, height)) {
    entity.x = newX;
    entity.y = newY;
  }

  // Animate mouth for Pacman
  if (!isGhost) {
    entity.mouthAngle = (entity.mouthAngle + 0.2) % (Math.PI * 2);
  }

  // For ghost, random direction change
  if (isGhost && Math.random() < 0.05) {
    entity.dir = getRandomAllowedDir(entity.x, entity.y, width, height);
  }
}